import { fetchAuthToken } from '#app/shared/apis/courier'
import { getUserProfile } from '#app/shared/apis/profile'
import { type AuthContextResult, getAuthContextForUser } from '#app/shared/contexts/AuthContext'
import { PagePaths } from '#app/shared/navigation/pagePaths'
import type { AuthUserWithTrueNorthExtensions, IdTokenWithTrueNorthExtensions } from '#app/shared/types/AuthTypes'
import type { EquipmentTypeResponseItem } from '#app/shared/types/EquipmentTypes'
import type { AuthenticatedUser, UserProfile } from '#app/shared/types/UserTypes'
import {
  checkSupportedBrokerAccountType,
  checkSupportedCarrierAccountType,
  checkSupportedStaffAccountType,
  getApiAuthenticatedUser,
  getAuthToken,
  getCypressIdToken,
  isCypress,
  isCypressAuthenticated,
  isCypressSignInTest,
  type JwtPayloadWithPermissions,
  resetAuthToken,
  setAuthToken
} from '#app/shared/utils/auth'
import * as DataDog from '#app/shared/utils/datadog'
import * as EventPublishing from '#app/shared/utils/eventPublishing'
import { fullstorySetUser } from '#app/shared/utils/fullstory'
import { setLDIdentifyUserFromContext } from '#app/shared/utils/launchDarkly'
import { loggerAuth0 } from '#app/shared/utils/logging'
import type { Auth0ContextInterface, LogoutOptions } from '@auth0/auth0-react'
import jwtDecode from 'jwt-decode'
import type { LDClient } from 'launchdarkly-react-client-sdk'
import { assign, fromPromise, log, setup } from 'xstate'
import { checkOnboardingStepNeeded, type OnboardingStepNeeded } from './utils'

export type AuthStateType = {
  /** Is authentication in a loading state? */
  authIsLoading: boolean
  /** Is the user authenticated? */
  authIsAuthenticated: boolean
  authError?: Error
  /** The user's authentication claims from */
  authUserClaims: IdTokenWithTrueNorthExtensions | null
  authProviderUser: AuthUserWithTrueNorthExtensions | null
  authPermissions: string[] | null
  authAuthenticatedUser: AuthenticatedUser | null
  authContext?: AuthContextResult
  authProviderAccessToken: string | null
  ldClient?: LDClient
  courierToken?: string
  onboardingStepNeeded: OnboardingStepNeeded | null
  userProfile?: UserProfile | null
  isBroker: boolean
  isCarrier: boolean
  isStaff: boolean
  isAppAccessAllowed: boolean
}

interface SignoutEvent {
  type: 'Signout'
  auth0Logout: (options?: LogoutOptions) => Promise<void>
  returnTo?: string
}

interface SignoutInput extends SignoutEvent {
  authIsAuthenticated: boolean
}

interface AuthenticateByAuthProviderEvent {
  type: 'AuthenticateByAuthProvider'
  auth0Context: Auth0ContextInterface<AuthUserWithTrueNorthExtensions>
  ldClient?: LDClient
  equipmentTypesMap: EquipmentTypeResponseItem[]
  apiIdentifier: string
}

interface AuthenticateByAuthProviderInput {
  context: AuthStateType
  event: AuthenticateByAuthProviderEvent
}

const fetchAuthenticatedUser = fromPromise(async () => {
  const response = await getApiAuthenticatedUser()

  return response.data
})

const auth0Signout = fromPromise<void, SignoutInput>(async ({ input }) => {
  if (!input.authIsAuthenticated) {
    return
  }

  // Convert path to full URL for Auth0 logout
  const returnTo = input.returnTo ?? PagePaths.Home
  const fullReturnTo = returnTo.startsWith('http')
    ? returnTo
    : `${window.location.origin}${returnTo}`

  await input.auth0Logout({
    logoutParams: {
      returnTo: fullReturnTo
    }
  })
})

const authenticateByAuthProvider = fromPromise<
  AuthStateType,
  AuthenticateByAuthProviderInput
>(async ({ input }) => {
  const { auth0Context, ldClient, equipmentTypesMap, apiIdentifier } = input.event

  if (!auth0Context) {
    throw new Error('auth0Context is missing')
  }

  if (!ldClient) {
    throw new Error('LDClient is missing')
  }

  // When running under Cypress and NOT a sign-in test, bypass authentication.
  const isCypressBypass = isCypressAuthenticated()

  if (
    !isCypressBypass
    && (!auth0Context.isAuthenticated || auth0Context.isLoading)
  ) {
    resetAuthToken()
    return {
      authIsLoading: auth0Context.isLoading,
      authIsAuthenticated: false,
      authContext: undefined,
      authPermissions: null,
      authUserClaims: null,
      authError: undefined,
      onboardingStepNeeded: null,
      isBroker: false,
      isCarrier: false,
      isStaff: false,
      ldClient
    } as AuthStateType
  }

  let accessToken: string | null = null
  let idTokenClaims: AuthUserWithTrueNorthExtensions | undefined = undefined

  if (!isCypressBypass) {
    accessToken = await auth0Context.getAccessTokenSilently({
      authorizationParams: {
        audience: apiIdentifier
      }
    })
    idTokenClaims = auth0Context.user

    setAuthToken(accessToken)
  } else {
    loggerAuth0.info('Authentication by Cypress')

    const id_token = getCypressIdToken() ?? ''
    idTokenClaims = jwtDecode<JwtPayloadWithPermissions>(id_token)
    // get access_token from cookies (setup by cypress)
    accessToken = getAuthToken() ?? null
  }

  if (!accessToken) {
    throw new Error('Access token is missing')
  }

  // Decode the token to get permissions
  const decoded = jwtDecode<JwtPayloadWithPermissions>(accessToken)

  const authState: AuthStateType = {
    authUserClaims: idTokenClaims,
    authProviderUser: idTokenClaims,
    authProviderAccessToken: accessToken,
    authPermissions: decoded.permissions,
    ldClient
  } as AuthStateType

  // Get authenticated user first
  const authenticatedUser = await getApiAuthenticatedUser()

  authState.authAuthenticatedUser = authenticatedUser.data
  authState.authContext = getAuthContextForUser(
    authState.authProviderUser as IdTokenWithTrueNorthExtensions,
    authState.authAuthenticatedUser
  )
  authState.authIsAuthenticated = authState.authContext.authenticated ?? false

  // Only fetch courier token if authenticated user is successful
  if (authenticatedUser.data) {
    const courierToken = await fetchAuthToken()
    authState.courierToken = courierToken
  }

  authState.onboardingStepNeeded = checkOnboardingStepNeeded(authState)

  EventPublishing.identifyUser(authState.authContext)
  DataDog.datadogSetUser(authState.authContext)
  fullstorySetUser(authState.authContext)
  await setLDIdentifyUserFromContext(authState.authContext, ldClient)

  if (!authState.authUserClaims) {
    authState.authUserClaims = {} as IdTokenWithTrueNorthExtensions
  }

  if (authState.authIsAuthenticated) {
    authState.isBroker = checkSupportedBrokerAccountType(authState) ?? false
    authState.isCarrier = checkSupportedCarrierAccountType(authState) ?? false
    authState.isStaff = checkSupportedStaffAccountType(authState) ?? false
    // Brokers and Staff do not have this additional check.
    if (authState.isCarrier) {
      authState.isAppAccessAllowed = authenticatedUser.data.is_app_access_allowed
    } else {
      authState.isAppAccessAllowed = true
    }
  } else {
    authState.isBroker = false
    authState.isCarrier = false
    authState.isStaff = false
    authState.isAppAccessAllowed = false
  }

  if (authState.isBroker) {
    authState.userProfile = await getUserProfile(equipmentTypesMap)
  }

  console.log(
    'assigning user_id',
    authState.authUserClaims.user_id,
    decoded.user_id
  )
  if (!authState.authUserClaims.user_id) {
    authState.authUserClaims.user_id = decoded.user_id
  }
  console.log('user_id', authState.authUserClaims.user_id)

  // Ensure permissions are set from the decoded token
  authState.authPermissions = decoded.permissions

  authState.authIsLoading = false

  return authState
})

export const userMachine = setup({
  actors: {
    fetchAuthenticatedUser,
    authenticateByAuthProvider,
    auth0Signout
  }
}).createMachine({
  types: {} as {
    context: AuthStateType
    events:
      | SignoutEvent
      | AuthenticateByAuthProviderEvent
      | {
        type: 'UnauthenticatedByAuthProvider'
        auth0Context: Auth0ContextInterface<AuthUserWithTrueNorthExtensions>
        ldClient?: LDClient
      }
      | {
        type: 'AuthenticatedByAuthProvider'
        token: string
        ldClient?: LDClient
      }
      | {
        type: 'UpdateUserProfile'
        userProfile: UserProfile | null
      }
  },
  id: 'UserStateMachine',
  initial: 'Unknown',
  context: {
    authIsLoading: true,
    authIsAuthenticated: false,
    authError: undefined,
    authUserClaims: null,
    authPermissions: null,
    authContext: undefined,
    courierToken: null,
    onboardingStepNeeded: null,
    userProfile: null,
    isBroker: false,
    isCarrier: false,
    isStaff: false
  },
  states: {
    Unknown: {
      on: {
        UnauthenticatedByAuthProvider: [
          {
            guard: () => {
              const cypressAuthenticated = isCypressAuthenticated()
              if (cypressAuthenticated) {
                console.log('Bypassing unauthentication for Cypress')
              }

              return !cypressAuthenticated
            },
            actions: [
              log('userMachine.UnauthenticatedByAuthProvider'),
              assign(({ event }) => {
                const { ldClient } = event
                return { ldClient }
              })
            ],
            target: 'Unauthenticated'
          },
          {
            guard: () => {
              const cypressAuthenticated = isCypressAuthenticated()
              return cypressAuthenticated
            },
            actions: [
              log('userMachine.UnauthenticatedByAuthProvider'),
              assign(({ event }) => {
                const { ldClient } = event
                return { ldClient }
              })
            ],
            target: 'AuthenticateUser'
          }
        ]
      }
    },
    Unauthenticated: {
      entry: [
        log('userMachine.Unauthenticated'),
        assign({
          authIsLoading: false,
          authIsAuthenticated: false,
          authError: undefined,
          authUserClaims: null,
          authPermissions: null,
          authContext: null,
          authProviderAccessToken: null,
          courierToken: null,
          onboardingStepNeeded: null,
          userProfile: null
        }),
        ({ context }) => {
          console.log({
            isCypress: isCypress(),
            isCypressSignInTest: isCypressSignInTest()
          })
          resetAuthToken()
          const authContext = getAuthContextForUser(null)
          EventPublishing.logoutUser()
          DataDog.datadogSetUser(authContext)
          fullstorySetUser(authContext)
          setLDIdentifyUserFromContext(authContext, context.ldClient)
        }
      ]
    },
    Ready: {
      entry: [
        log('userMachine.Ready'),
        assign(({ context }) => ({
          authIsLoading: false,
          authIsAuthenticated: context.authContext?.authenticated ?? false
        })),
        ({ context }) => console.log('Debug context', context),
        ({ context }) => console.log('Authenticated', context.authContext)
      ],
      on: {
        AuthenticateByAuthProvider: {
          // ignore AuthenticateByAuthProvider events when already authenticated
          actions: [
            log('userMachine.Ready.AuthenticateByAuthProvider'),
            ({ context, event }) => {
              if (context.ldClient !== event.ldClient) {
                // if the ldClient has changed, update the ldClient with user identity.
                console.log('Updating ldClient with user identity')
                setLDIdentifyUserFromContext(
                  context.authContext,
                  event.ldClient
                )
              }
            }
          ]
        }
      }
    },
    AuthenticateUser: {
      entry: [log('userMachine.AuthenticateUser')],
      invoke: {
        input: ({ event, context }) => ({
          event: event as AuthenticateByAuthProviderEvent,
          context: context as AuthStateType
        }),
        src: 'authenticateByAuthProvider',
        onDone: {
          actions: [assign(({ event }) => event.output)],
          target: 'Ready'
        },
        onError: {
          target: 'Failed'
        }
      }
    },
    Failed: {
      entry: [
        log('userMachine.Failed'),
        ({ event }) =>
          console.error('User authentication failed with error: ', {
            error: event.error
          }),
        assign(({ event }) => ({
          authIsLoading: false,
          authError: event.error,
          authIsAuthenticated: false,
          authUserClaims: null,
          authPermissions: null,
          authContext: null,
          authProviderAccessToken: null,
          courierToken: null,
          onboardingStepNeeded: null,
          userProfile: null
        }))
      ]
    },
    SigningOut: {
      on: {
        Signout: {
          actions: [log('userMachine.SigningOut.Signout')]
        }
      },
      entry: [
        log('userMachine.SigningOut'),
        assign({
          authIsLoading: true
        })
      ],
      invoke: {
        input: ({ event, context }) =>
          ({
            ...event,
            authIsAuthenticated: context.authIsAuthenticated
          }) as SignoutInput,
        src: 'auth0Signout',
        onDone: {
          actions: [log('userMachine.SigningOut.onDone')],
          target: 'Unauthenticated'
        },
        onError: {
          actions: [log('userMachine.SigningOut.onError')],
          target: 'Unauthenticated'
        }
      }
    }
  },
  on: {
    Signout: {
      actions: [log('userMachine.Signout')],
      target: '.SigningOut',
      guard: ({ context }) => context.authIsAuthenticated
    },
    AuthenticateByAuthProvider: {
      actions: [
        log('userMachine.AuthenticateByAuthProvider'),
        log(({ event }) => event)
      ],
      target: '.AuthenticateUser'
    },
    UpdateUserProfile: {
      actions: [
        log('userMachine.UpdateUserProfile'),
        assign(({ event, context }) => ({
          userProfile: event.userProfile,
          onboardingStepNeeded: checkOnboardingStepNeeded({
            ...context,
            userProfile: event.userProfile
          } as AuthStateType)
        })),
        ({ context }) => console.log('Updated userProfile:', context.userProfile)
      ]
    }
  }
})
